/**
 * FrameworkRoll — the report's starred frameworks as Carbon framework tiles
 * (DESIGN.md "Framework tile", mockup carbon/03) in a condensed grid: the
 * framework name with its code, a 2rem light score, one sentence keyed to
 * its band, the period delta tag, an 8px bar in the Best Practice mark color
 * and a helper line with the control count.
 *
 * Weakest first, so the eye lands on what needs attention before the
 * frameworks above 80. A low score is never colored (the Neutral Bad State
 * Rule): the band reads from the sentence and the bar length. Frameworks
 * folded into the Guardian Score carry an "In score" tag.
 */

import clsx from 'clsx'
import { BigNumber, CondensedGrid, ScoreBar, Tag, Tile, TileHeader } from '@/components/carbon'
import type { ExecutiveReportData } from '@/types/executiveReport'
import { deltaInDays, frameworkGrid, frameworkVerdict, orderFrameworks } from './report-view'

type Framework = ExecutiveReportData['frameworkScores'][number]

interface Props {
  frameworks: Framework[]
  /** Reporting window in days, for each framework's delta tag ("+4 in 30 days"). Without it the tags are hidden. */
  periodDays?: number
}

export default function FrameworkRoll({ frameworks, periodDays }: Props) {
  if (!frameworks || frameworks.length === 0) {
    return (
      <p className="m-0 border-t border-line-subtle py-6 text-helper text-text-helper">
        No starred frameworks for this period. Star a framework on the Best Practice tab to see it here.
      </p>
    )
  }

  const ordered = orderFrameworks(frameworks)
  const grid = frameworkGrid(ordered.length)

  return (
    <CondensedGrid as="ul" aria-label="Frameworks, weakest first" columns={grid.columns}>
      {ordered.map((f, index) => (
        <FrameworkTile
          key={f.code}
          framework={f}
          isLowest={index === 0}
          periodDays={periodDays}
          className={index === ordered.length - 1 ? grid.lastClassName : undefined}
        />
      ))}
    </CondensedGrid>
  )
}

function FrameworkTile({
  framework,
  isLowest,
  periodDays,
  className,
}: {
  framework: Framework
  isLowest: boolean
  periodDays?: number
  className?: string
}) {
  const delta = periodDays ? deltaInDays(framework.change, periodDays) : null
  return (
    <Tile as="li" className={clsx('flex min-w-0 flex-col', className)}>
      <TileHeader
        title={framework.name}
        level={3}
        aside={framework.includeInGuardianScore ? <Tag size="sm">In score</Tag> : undefined}
      />
      <p className="m-0 mt-0.5 text-helper text-text-helper">{framework.code}</p>
      <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-2">
        <BigNumber value={framework.score} size="figure" suffix="%" as="span" />
        {delta ? <Tag className="tabular-nums">{delta}</Tag> : null}
      </div>
      <p className="m-0 mt-3 text-body text-text-secondary [overflow-wrap:anywhere]">
        {frameworkVerdict(framework.name, framework.score, isLowest)}
      </p>
      <div className="mt-auto pt-5">
        <ScoreBar value={framework.score} category="bpa" label={`${framework.name} score`} />
        <p className="m-0 mt-2 text-helper text-text-helper">
          {framework.passedControls} of {framework.totalControls} controls pass
        </p>
      </div>
    </Tile>
  )
}
